<template>
  <v-container fluid grid-list-xl>
    <v-layout row wrap>
      <v-flex xs12>
        <h1>
          {{ $tc('models.user', 2) }}
          <v-btn color="primary" @click="goTo('newUser')"> {{ $t('create') }} </v-btn>
        </h1>
      </v-flex>

      <v-flex xs12>
        <v-card>
          <v-card-text>
            <v-layout wrap>
              <v-flex xs12 md4>
                <v-text-field v-model="search"
                              append-icon="search"
                              :label="$t('search')"
                              @click:append="fetch"
                              @keyup.enter.native="fetch"
                              clearable
                              ></v-text-field>
              </v-flex>

              <v-flex xs12 md4>
                <v-select v-model="field"
                          :items="fieldList"
                          item-value="id"
                          item-text="name"
                          ></v-select>
              </v-flex>

              <v-flex xs12 md4>
                <v-select v-model="status"
                          :items="statuses"
                          item-value="id"
                          item-text="name"
                          :label="$tc('attrs.status')"
                          @change="fetch"
                          ></v-select>
              </v-flex>

              <v-flex xs12 md4>
                <v-select v-model="special_carriers"
                          :items="carriers"
                          item-value="id"
                          item-text="name"
                          :label="$tc('attrs.special_carriers')"
                          @change="fetch"
                          ></v-select>
              </v-flex>

              <v-flex xs12 md4>
                <v-select v-model="user_price"
                          :items="user_prices"
                          item-value="id"
                          item-text="name"
                          :label="$tc('attrs.price')"
                          @change="fetch"
                          ></v-select>
              </v-flex>

              <v-flex xs12 md6>
                <v-btn color="primary" @click="updateFilters('clearAll')"> {{ $t('clearAllFilters') }} </v-btn>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-data-table class="elevation-1"
                      :headers="headers"
                      :items="items"
                      :loading="loading"
                      :total-items="totalItems"
                      :pagination.sync="pagination"
                      item-key="id"
                      :no-data-text="$t('noDataAvailable')">
          <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
          <template v-slot:items="props">
            <tr @click="props.expanded = !props.expanded">
              <td>
                {{ props.item.sequential_id || props.item.id }}
              </td>
              <td> {{ props.item.fullname }} </td>
              <td> {{ props.item.email }} </td>
              <td> {{ $t(`list.userTypes.${props.item.user_type}`) }} </td>
              <td>
                <font-awesome-icon v-if="(props.item.user_prices_count && props.item.user_prices_count > 0)" icon="check" color="green"></font-awesome-icon>
                <font-awesome-icon v-else icon="times" color="red"></font-awesome-icon>
              </td>
              <td class="pa-0 pr-2 text-xs-right">
                <v-btn color="primary" flat icon v-if="!props.expanded">
                  <font-awesome-icon icon="angle-left"></font-awesome-icon>
                </v-btn>

                <v-btn color="primary" flat icon v-if="props.expanded">
                  <font-awesome-icon icon="angle-down"></font-awesome-icon>
                </v-btn>
              </td>
            </tr>
          </template>

          <template v-slot:expand="props">
            <v-layout row wrap class="ma-1">
              <v-flex xs12 md6>
                <p>
                {{ $t('attrs.phone') }}
                <strong> {{ props.item.phone }} </strong>
                </p>

                <p>
                {{ $t('attrs.balance') }}
                <strong> {{ props.item.balance | currency }} </strong>
                </p>

                <p>
                {{ $t('attrs.created_at') }}
                <strong> {{ props.item.created_at | date('lll') }} </strong>
                </p>

                <p v-if="props.item.parent">
                {{ $t('attrs.parent') }}
                <strong> {{ props.item.parent.email }} </strong>
                </p>

                <p>
                {{ $t('attrs.last_sign_in_at') }}
                <strong> {{ props.item.current_sign_in_at | date('lll') }} </strong>
                </p>

                <div v-if="showReferralCode">
                  <p>
                  {{ $t('attrs.referral_code') }}
                  <strong> {{ props.item.referral_code }} </strong>
                  </p>

                  <p>
                  {{ $t('attrs.referred_by') }}
                  <strong> {{ props.item.referred_by }} </strong>
                  </p>
                </div>

                <p>
                {{ $t('attrs.notes') }}
                <strong> {{ props.item.notes }} </strong>
                </p>
              </v-flex>

              <v-flex xs12 md6 class="text-xs-right">
                <StatusChip :status="props.item.status" outline></StatusChip>

                <v-btn color="primary" flat @click="goTo('userCarriers', { userId: props.item.id })" v-if="props.item.special_carriers">
                  {{ $tc('models.carrier', 2) }}
                </v-btn>

                <v-btn color="primary" flat @click="goTo('userTransactions', { userId: props.item.id})">
                  {{ $tc('models.transaction', 2) }}
                </v-btn>

                <v-btn color="primary" flat @click="goTo('userPrices', { userId: props.item.id })">
                  {{ $t('prices') }}
                </v-btn>

                <v-btn color="primary" flat @click="goTo('userFiscalData', { userId: props.item.id })">
                  {{ $tc('models.fiscalDatum', 2) }}
                </v-btn>

                <v-btn color="primary" flat @click="goTo('subusers', { userId: props.item.id })" v-if="!props.item.parent_id">
                  {{ $tc('models.subuser', 2) }}
                </v-btn>

                <v-btn color="primary" flat @click="confirmEmail(props.item)" v-if="!props.item.confirmed_at">
                  {{ $t('confirmEmail') }}
                </v-btn>

                <v-btn color="primary" flat icon @click="goTo('editUser', props.item.id)">
                  <font-awesome-icon icon="edit"></font-awesome-icon>
                </v-btn>

                <v-btn color="success" flat icon @click="activate(props.item)" v-if="props.item.status !== 'active'">
                  <font-awesome-icon icon="check"></font-awesome-icon>
                </v-btn>

                <v-btn color="red" flat icon @click="suspend(props.item)" v-if="props.item.status !== 'suspended'">
                  <font-awesome-icon icon="ban"></font-awesome-icon>
                </v-btn>
                <template v-if="$auth.isDev()">
                  <br>
                  <v-btn color="red" outline v-if="props.item.export_csv_visible" @click="toggleCsvExportVisible(props.item)">
                    {{ $t('exportCsv') }}
                  </v-btn>

                  <v-btn color="green" outline v-else @click="toggleCsvExportVisible(props.item)">
                    {{ $t('exportCsv') }}
                  </v-btn>
                </template>

                <v-btn :color="props.item.fiscal_data_verified ? 'green' : 'red'" outline @click="updateFiscalDataVerified(props.item)" v-if="props.item.user_type == 'customer'">
                  {{ $t('verifyUser') }}
                </v-btn>
              </v-flex>
            </v-layout>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import User from '@/client/user'
import Setting from '@/client/setting'

export default {
  name: 'UsersView',

  props: {},

  data () {
    return {
      headers: [
        { text: '#', align: 'left', sortable: true, value: 'id', width: '5%' },
        { text: this.$t('attrs.name'), align: 'left', sortable: true, value: 'name' },
        { text: this.$t('attrs.email'), align: 'left', sortable: true, value: 'email' },
        { text: this.$t('attrs.user_type'), align: 'left', sortable: true, value: 'user_type' },
        { text: this.$t('personailsedPrices'), align: 'left', sortable: true, value: 'user_prices_count' },
        { text: '', align: 'left', sortable: false, value: '' }
      ],
      items: [],
      totalItems: 0,
      pagination: {
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true
      },
      loading: false,

      search: '',

      field: 'email',
      fieldList: [
        { id: 'email', name: this.$t('attrs.email') },
        { id: 'id', name: '#' },
        { id: 'name', name: this.$t('attrs.name') },
        { id: 'last_name', name: this.$t('attrs.last_name') },
        { id: 'phone', name: this.$t('attrs.phone') },
        { id: 'referral_code', name: this.$t('attrs.referral_code') }
      ],

      status: '',
      statuses: [
        { id: '', name: this.$t('list.statuses.all') },
        { id: '0', name: this.$t('list.statuses.active') },
        { id: '1', name: this.$t('list.statuses.suspended') }
      ],

      special_carriers: '',
      carriers: [
        { id: '', name: this.$t('list.statuses.all') },
        { id: 'false', name: this.$t('excludeCarrier') },
        { id: 'true', name: this.$t('includeCarrier') }
      ],

      user_price: '',
      user_prices: [
        { id: '', name: this.$t('list.statuses.all') },
        { id: '1', name: this.$t('includeCustomPrice') }
      ],

      referralSetting: {}
    }
  },

  components: {
  },

  computed: {
    showReferralCode () {
      return this.referralSetting.value === '1'
    }
  },

  methods: {
    async fetch () {
      this.loading = true
      try {
        const { sortBy, descending, page, rowsPerPage } = this.pagination
        let order = ''
        let varyPrices = false

        if (sortBy) {
          order = `${sortBy} ${descending ? 'desc' : 'asc'}`
        }

        let where = []
        if (this.search) {
          if (this.field === 'id') {
            where = [`id = ?`, this.search]
          } else {
            where = [`${this.field} ilike ?`, `%${this.search}%`]
          }
        }

        if (this.status) {
          if (where.length) {
            where[0] += `AND status = ?`
          } else {
            where.push('status = ?')
          }
          where.push(this.status)
        }

        if (this.special_carriers) {
          if (where.length) {
            where[0] += `AND special_carriers = ?`
          } else {
            where.push('special_carriers = ?')
          }
          where.push(this.special_carriers)
        }

        if(this.user_price) {
          varyPrices = true
        }

        //update filters
        this.updateFilters('fetch')

        this.items = await User.all({
          page,
          per_page: rowsPerPage,
          where,
          order,
          vary_prices: varyPrices
        })

        // if (this.user_price){
        //   this.items = this.items.filter(user => user.user_prices.length > 0)
        //   this.totalItems = this.items.length
        // } else {
        // }

        this.totalItems = this.items.pagination.total_objects
      } catch (e) {
        console.error(e)
      }
      this.loading = false
    },

    async activate (user) {
      try {
        user.status = 'active'
        user.activate_user_action = true
        await user.save()
      } catch (err) {
        // empty
      }
      this.fetch()
    },

    async suspend (user) {
      try {
        user.status = 'suspended'
        await user.save()
      } catch (err) {
        // empty
      }
      this.fetch()
    },

    async toggleCsvExportVisible (user) {
      try {
        user.export_csv_visible = !user.export_csv_visible
        await user.save()
      } catch (err) {
        // empty
      }

      this.fetch()
    },

    async confirmEmail (user) {
      try {
        await user.confirmEmail()
      } catch (err) {
        // empty
      }
      this.fetch()
    },

    async updateFiscalDataVerified (user) {
      if ( !this.confirmation() ) { return }

      try {
        await user.updateFiscalDataVerified(user.id)
      } catch (err) {
        console.error(err)
      }
      this.fetch()
    },

    confirmation () {
      return window.confirm(this.$t('sure'))
    },

    updateFilters(action) {
      const keys = Object.keys(this.$store.state.userFilters)

      if (action == 'fetch') {
        keys.forEach((key) => {
          this.$store.state.userFilters[key] = this[key]
        });
      } else if (action == 'mounted') {
        keys.forEach((key) => {
          this[key] = this.$store.state.userFilters[key]
        });
      } else {
        //clear all filters
        keys.forEach((key) => {
          this.$store.state.userFilters[key] = (key !== 'field') ? '' : 'email'; 
          this[key] = (key !== 'field') ? '' : 'email'; 
        });

        this.fetch()
      }
    }
  },

  watch: {
    pagination: {
      handler () {
        this.fetch()
      },
      deep: true
    }
  },

  async mounted () {
    //updating current filters from store
    if (this.$route.params.userFiltersStore) {
      this.updateFilters('mounted')
    } else {
      this.updateFilters('clearAll')
    }

    this.fetch()
    this.referralSetting = await Setting.byKey('referral_code')
  }
}
</script>
